export interface QuestionWithSourceNodes {
  sourceNodeIds?: string[]
  sourceNodeLabels?: string[]
  sourceNodeCategories?: string[]
  /** @deprecated Legacy pre-holistic singular fields. Used as fallback. */
  sourceNodeId?: string
  sourceNodeLabel?: string
  sourceNodeCategory?: string
}

export interface ValidatedNode {
  id: string
  label: string
  category: string | null
  /** Number of questions in this validation that target this node. */
  questionCount: number
}

/**
 * Aggregate per-question source-node arrays into a deduped list of nodes.
 *
 * Handles both the holistic array form (`sourceNodeIds[]`) and the legacy
 * singular form (`sourceNodeId`). Falls back to label-based identity if a
 * question has labels without ids (older snapshots).
 */
export function aggregateValidatedNodes(questions: QuestionWithSourceNodes[]): ValidatedNode[] {
  const byKey = new Map<string, ValidatedNode>()

  for (const q of questions) {
    const ids = q.sourceNodeIds ?? (q.sourceNodeId ? [q.sourceNodeId] : [])
    const labels = q.sourceNodeLabels ?? (q.sourceNodeLabel ? [q.sourceNodeLabel] : [])
    const categories =
      q.sourceNodeCategories ?? (q.sourceNodeCategory ? [q.sourceNodeCategory] : [])

    // Iterate the longer of ids/labels — both arrays should be parallel, but
    // legacy data may have only one side populated.
    const length = Math.max(ids.length, labels.length)
    for (let i = 0; i < length; i++) {
      const id = ids[i] ?? `__label_${labels[i] ?? ''}`
      if (!id || id === '__label_') continue
      const existing = byKey.get(id)
      if (existing) {
        existing.questionCount += 1
        if (!existing.label && labels[i]) existing.label = labels[i]
        if (!existing.category && categories[i]) existing.category = categories[i]
      } else {
        byKey.set(id, {
          id,
          label: labels[i] ?? '(unknown node)',
          category: categories[i] ?? null,
          questionCount: 1,
        })
      }
    }
  }

  return Array.from(byKey.values())
}

const CATEGORY_ORDER: Record<string, number> = {
  need: 0,
  idea: 1,
  venture: 2,
}

export function groupNodesByCategory(
  nodes: ValidatedNode[]
): { category: string; nodes: ValidatedNode[] }[] {
  const groups = new Map<string, ValidatedNode[]>()
  for (const node of nodes) {
    const key = node.category ?? 'other'
    const bucket = groups.get(key) ?? []
    bucket.push(node)
    groups.set(key, bucket)
  }

  return Array.from(groups.entries())
    .map(([category, nodes]) => ({
      category,
      nodes: nodes.sort((a, b) => b.questionCount - a.questionCount),
    }))
    .sort((a, b) => {
      const aOrder = CATEGORY_ORDER[a.category] ?? 99
      const bOrder = CATEGORY_ORDER[b.category] ?? 99
      return aOrder - bOrder
    })
}
